import axios from 'axios';
import type {
  HandlingsCoachJobResult,
  HandlingsCoachJobStatus,
  HandlingsCoachV2Section,
} from 'src/contracts/oth';

export const AI_COACH_FAILED_USER_MESSAGE =
  'Analysen feilet. Trykk «Prøv igjen».';

export const AI_COACH_RETRYING_USER_MESSAGE = 'Prøver igjen …';

export const AI_COACH_STILL_RUNNING_USER_MESSAGE =
  'Analysen fortsetter i bakgrunnen.';

export const AI_COACH_START_FAILED_USER_MESSAGE =
  'Kunne ikke starte analysen. Trykk «Prøv igjen».';

export const AI_COACH_POLL_BUDGET_MS = 180_000;
export const AI_COACH_POLL_DELAYS_MS = [1000, 2000, 3000] as const;
export const AI_COACH_POLL_MAX_DELAY_MS = 5000;
export const AI_COACH_START_ATTEMPTS = 2;
export const AI_COACH_CACHE_POLL_INTERVAL_MS = 4000;
export const AI_COACH_BACKGROUND_WATCH_MS = 10 * 60 * 1000;

export class AiCoachJobFailedError extends Error {
  readonly errorCode: string | null;

  constructor(errorCode: string | null) {
    super(AI_COACH_FAILED_USER_MESSAGE);
    this.name = 'AiCoachJobFailedError';
    this.errorCode = errorCode;
  }
}

function messageFromErrorBody(data: unknown): string {
  if (typeof data === 'string') {
    return data.trim();
  }
  if (!data || typeof data !== 'object') {
    return '';
  }
  const record = data as { message?: unknown; error?: unknown };
  if (typeof record.message === 'string' && record.message.trim()) {
    return record.message.trim();
  }
  if (typeof record.error === 'string' && record.error.trim()) {
    return record.error.trim();
  }
  return '';
}

export function isAiCoachValidationError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }
  const status = error.response?.status;
  return status === 400 || status === 422;
}

export function isAiCoachTransportError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;
  if (status === 504 || status === 502 || status === 408) {
    return true;
  }

  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
    return true;
  }

  return !error.response && error.message === 'Network Error';
}

export function getAiCoachUserErrorMessage(error: unknown): string {
  if (error instanceof AiCoachJobFailedError) {
    return error.message;
  }

  if (isAiCoachValidationError(error) && axios.isAxiosError(error)) {
    const status = error.response?.status;
    const fromBody = messageFromErrorBody(error.response?.data);
    if (fromBody) {
      return status ? `HTTP ${status}: ${fromBody}` : fromBody;
    }
    if (status) {
      return `HTTP ${status}`;
    }
  }

  if (isAiCoachTransportError(error)) {
    return AI_COACH_START_FAILED_USER_MESSAGE;
  }

  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const fromBody = messageFromErrorBody(error.response?.data);
    if (fromBody) {
      return status ? `HTTP ${status}: ${fromBody}` : fromBody;
    }
    if (status) {
      return `HTTP ${status}`;
    }
  }

  if (
    error instanceof Error &&
    error.message.trim() &&
    error.message !== 'Network Error'
  ) {
    return error.message;
  }

  return 'Kunne ikke hente AI-handlingscoach.';
}

export function pollDelayMs(
  pollIndex: number,
  random: () => number = Math.random
): number {
  const base =
    pollIndex < AI_COACH_POLL_DELAYS_MS.length
      ? AI_COACH_POLL_DELAYS_MS[pollIndex]
      : AI_COACH_POLL_MAX_DELAY_MS;
  return base + Math.round(base * 0.2 * random());
}

export function pickCachedSection(
  results: Record<string, HandlingsCoachV2Section>,
  boxId: string,
  matchesCurrentInput: (
    boxId: string,
    section: HandlingsCoachV2Section | undefined
  ) => boolean
): HandlingsCoachV2Section | undefined {
  const section =
    results[boxId] ??
    Object.values(results).find((item) => item.box_id === boxId);

  if (!section) {
    return undefined;
  }

  if (!section.user_input) {
    return section;
  }

  return matchesCurrentInput(boxId, section) ? section : undefined;
}

export async function waitForCachedAiSection(options: {
  boxId: string;
  fetchCachedResults: () => Promise<Record<string, HandlingsCoachV2Section>>;
  matchesCurrentInput: (
    boxId: string,
    section: HandlingsCoachV2Section | undefined
  ) => boolean;
  timeoutMs: number;
  intervalMs?: number;
  shouldAbort?: () => boolean;
  sleep?: (ms: number) => Promise<void>;
}): Promise<HandlingsCoachV2Section | null> {
  const intervalMs = options.intervalMs ?? AI_COACH_CACHE_POLL_INTERVAL_MS;
  const sleep =
    options.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  const maxAttempts = Math.max(
    1,
    Math.floor(options.timeoutMs / intervalMs) + 1
  );

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (options.shouldAbort?.()) {
      return null;
    }

    try {
      const results = await options.fetchCachedResults();
      const section = pickCachedSection(
        results,
        options.boxId,
        options.matchesCurrentInput
      );
      if (section) {
        return section;
      }
    } catch {
      // Cache reads can fail while the worker is still writing.
    }

    if (attempt < maxAttempts - 1) {
      await sleep(intervalMs);
    }
  }

  return null;
}

export type AnalyzeHandlingsCoachBoxOutcome =
  | { kind: 'section'; section: HandlingsCoachV2Section }
  | { kind: 'superseded'; job: HandlingsCoachJobResult }
  | { kind: 'still_running'; job: HandlingsCoachJobResult }
  | { kind: 'aborted' };

export type AnalyzeBoxJobUiStatus = Extract<
  HandlingsCoachJobStatus,
  'queued' | 'processing' | 'retrying'
>;

const ANALYZE_BUTTON_ACTIVE_STATUSES: ReadonlySet<string> = new Set([
  'queued',
  'processing',
  'retrying',
]);

export function isAnalyzeButtonBusy(
  isStarting: boolean,
  boxJobUiStatus: Record<string, string | undefined>,
  results: Record<string, unknown>
): boolean {
  if (isStarting) {
    return true;
  }

  return Object.entries(boxJobUiStatus).some(([boxId, status]) => {
    if (!status || !ANALYZE_BUTTON_ACTIVE_STATUSES.has(status)) {
      return false;
    }
    return results[boxId] == null;
  });
}

async function fetchMatchingSection(options: {
  boxId: string;
  fetchCachedResults: () => Promise<Record<string, HandlingsCoachV2Section>>;
  matchesCurrentInput: (
    boxId: string,
    section: HandlingsCoachV2Section | undefined
  ) => boolean;
}): Promise<HandlingsCoachV2Section | undefined> {
  try {
    const results = await options.fetchCachedResults();
    return pickCachedSection(
      results,
      options.boxId,
      options.matchesCurrentInput
    );
  } catch {
    return undefined;
  }
}

async function startJobWithTransportRetry(options: {
  boxId: string;
  startJob: (boxId: string) => Promise<HandlingsCoachJobResult>;
  shouldAbort?: () => boolean;
  sleep: (ms: number) => Promise<void>;
  startAttempts: number;
}): Promise<HandlingsCoachJobResult | 'aborted'> {
  let lastError: unknown;

  for (let attempt = 0; attempt < options.startAttempts; attempt++) {
    if (options.shouldAbort?.()) {
      return 'aborted';
    }

    try {
      return await options.startJob(options.boxId);
    } catch (error) {
      if (isAiCoachValidationError(error)) {
        throw error;
      }
      lastError = error;
      const canRetry =
        isAiCoachTransportError(error) && attempt < options.startAttempts - 1;
      if (!canRetry) {
        throw error;
      }
      await options.sleep(pollDelayMs(attempt, () => 0));
    }
  }

  throw lastError;
}

export async function analyzeHandlingsCoachBox(options: {
  boxId: string;
  startJob: (boxId: string) => Promise<HandlingsCoachJobResult>;
  getJob: (jobId: string) => Promise<HandlingsCoachJobResult>;
  fetchCachedResults: () => Promise<Record<string, HandlingsCoachV2Section>>;
  matchesCurrentInput: (
    boxId: string,
    section: HandlingsCoachV2Section | undefined
  ) => boolean;
  shouldAbort?: () => boolean;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  random?: () => number;
  pollBudgetMs?: number;
  startAttempts?: number;
  onStatus?: (status: HandlingsCoachJobStatus) => void;
}): Promise<AnalyzeHandlingsCoachBoxOutcome> {
  const sleep =
    options.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  const now = options.now ?? Date.now;
  const random = options.random ?? Math.random;
  const pollBudgetMs = options.pollBudgetMs ?? AI_COACH_POLL_BUDGET_MS;
  const startAttempts = options.startAttempts ?? AI_COACH_START_ATTEMPTS;

  const loadSection = () =>
    fetchMatchingSection({
      boxId: options.boxId,
      fetchCachedResults: options.fetchCachedResults,
      matchesCurrentInput: options.matchesCurrentInput,
    });

  const started = await startJobWithTransportRetry({
    boxId: options.boxId,
    startJob: options.startJob,
    shouldAbort: options.shouldAbort,
    sleep,
    startAttempts,
  });

  if (started === 'aborted') {
    return { kind: 'aborted' };
  }

  let job = started;
  options.onStatus?.(job.status);

  if (job.status === 'failed') {
    throw new AiCoachJobFailedError(job.errorCode);
  }
  if (job.status === 'superseded') {
    return { kind: 'superseded', job };
  }

  if (job.status === 'completed' || job.cacheHit) {
    const section = await loadSection();
    if (section) {
      return { kind: 'section', section };
    }
    if (!job.jobId) {
      throw new Error('Kunne ikke hente AI-handlingscoach.');
    }
  } else if (!job.jobId) {
    throw new Error('Kunne ikke hente AI-handlingscoach.');
  }

  const jobId = job.jobId;
  if (!jobId) {
    throw new Error('Kunne ikke hente AI-handlingscoach.');
  }

  const deadline = now() + pollBudgetMs;
  let pollIndex = 0;

  while (now() < deadline) {
    if (options.shouldAbort?.()) {
      return { kind: 'aborted' };
    }

    await sleep(pollDelayMs(pollIndex, random));
    pollIndex += 1;

    if (options.shouldAbort?.()) {
      return { kind: 'aborted' };
    }

    const cachedSection = await loadSection();
    if (cachedSection) {
      return { kind: 'section', section: cachedSection };
    }

    try {
      job = await options.getJob(jobId);
      options.onStatus?.(job.status);
    } catch (error) {
      if (isAiCoachValidationError(error)) {
        throw error;
      }
      if (now() >= deadline) {
        return { kind: 'still_running', job };
      }
      continue;
    }

    if (job.status === 'failed') {
      throw new AiCoachJobFailedError(job.errorCode);
    }
    if (job.status === 'superseded') {
      return { kind: 'superseded', job };
    }

    if (job.status === 'completed' || job.cacheHit) {
      const section = await loadSection();
      if (section) {
        return { kind: 'section', section };
      }
    }
  }

  return { kind: 'still_running', job };
}
